import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STATUSES = [
    { value: 'active', label: 'Active' },
    { value: 'acknowledged', label: 'Ack' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'all', label: 'All' },
];

const TYPES = [
    { value: 'sales_spike',           label: 'Spike' },
    { value: 'sales_drop',            label: 'Drop' },
    { value: 'zero_sales_streak',     label: 'Zero Streak' },
    { value: 'invoice_gap',           label: 'Invoice Gap' },
    { value: 'revenue_share_anomaly', label: 'Flight Risk' },
    { value: 'volume_anomaly',        label: 'Volume' },
];

const SEVERITIES = [
    { value: 'critical', label: 'Critical' },
    { value: 'high',     label: 'High' },
    { value: 'medium',   label: 'Medium' },
    { value: 'low',      label: 'Low' },
    { value: 'warning',  label: 'Warning' },
    { value: 'info',     label: 'Info' },
];

/**
 * Controlled filter rail for /v3/anomalies. Pushes new filter state up via
 * onChange. Tenant search is debounced 300ms.
 */
export default function AnomaliesFilters({ filters, onChange }) {
    const { t } = useTranslation();
    const [tenantInput, setTenantInput] = useState(filters.tenant_id || '');
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if ((filters.tenant_id || '') !== tenantInput) {
                onChange({ ...filters, tenant_id: tenantInput || undefined });
            }
        }, 300);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantInput]);

    const toggleArr = (key, value) => {
        const cur = Array.isArray(filters[key]) ? filters[key] : [];
        const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
        onChange({ ...filters, [key]: next.length ? next : undefined });
    };

    const setStatus = (status) => onChange({ ...filters, status });

    const setDate = (key, value) => {
        onChange({ ...filters, [key]: value || undefined });
    };

    const reset = () => {
        setTenantInput('');
        onChange({ status: 'active' });
    };

    const types = Array.isArray(filters.alert_type) ? filters.alert_type : [];
    const sevs = Array.isArray(filters.severity) ? filters.severity : [];

    return (
        <aside className="v3-anomalies-filters">
            <h4>{t('Filters')}</h4>

            <div className="group">
                <label>{t('Status')}</label>
                <div className="v3-anomalies-segmented">
                    {STATUSES.map(s => (
                        <button
                            key={s.value}
                            className={(filters.status || 'active') === s.value ? 'active' : ''}
                            onClick={() => setStatus(s.value)}
                            type="button"
                        >{t(s.label)}</button>
                    ))}
                </div>
            </div>

            <div className="group">
                <label>{t('Alert Type')}</label>
                <div className="v3-anomalies-chips">
                    {TYPES.map(ty => (
                        <button
                            key={ty.value}
                            type="button"
                            className={`v3-anomalies-chip ${types.includes(ty.value) ? 'active' : ''}`}
                            onClick={() => toggleArr('alert_type', ty.value)}
                        >{t(ty.label)}</button>
                    ))}
                </div>
            </div>

            <div className="group">
                <label>{t('Severity')}</label>
                <div className="v3-anomalies-chips">
                    {SEVERITIES.map(s => (
                        <button
                            key={s.value}
                            type="button"
                            className={`v3-anomalies-chip ${sevs.includes(s.value) ? 'active' : ''}`}
                            onClick={() => toggleArr('severity', s.value)}
                        >{t(s.label)}</button>
                    ))}
                </div>
            </div>

            <div className="group">
                <label>{t('Tenant ID')}</label>
                <input
                    type="text"
                    className="v3-anomalies-input"
                    placeholder={t('exact tenant_id')}
                    value={tenantInput}
                    onChange={(e) => setTenantInput(e.target.value)}
                />
            </div>

            <div className="group">
                <label>{t('Date range')}</label>
                <div className="v3-anomalies-date-row">
                    <input
                        type="date"
                        className="v3-anomalies-input"
                        value={filters.from || ''}
                        onChange={(e) => setDate('from', e.target.value)}
                    />
                    <input
                        type="date"
                        className="v3-anomalies-input"
                        value={filters.to || ''}
                        onChange={(e) => setDate('to', e.target.value)}
                    />
                </div>
            </div>

            <button
                type="button"
                className="v3-btn v3-btn-ghost"
                onClick={reset}
                style={{ marginTop: 4 }}
            >{t('Reset filters')}</button>
        </aside>
    );
}
